/**
 * Exercise library (docs/PRODUCT.md §8) — follows the physical therapist's list
 * of 2026-10-16: 27 movements in four groups (cervical, thoracic, posture,
 * stretches), each with set-up steps, common mistakes, when to ease off and a
 * read-aloud voice guide. Nothing needs equipment beyond a wall, a chair or
 * desk, the floor, a doorway or a towel.
 *
 * "Exercise snacks" (programs, rest breaks, Darwin's picks) are short bouts
 * built from these same movements — there is no separate snack list.
 *
 * Wording rules (LEGAL §4): these are general movements for desk posture and
 * mobility. No entry claims to treat, relieve or fix anything, and tags describe
 * the body region and movement only — never a symptom or condition. The
 * general stop-if-pain line lives in DISCLAIMERS (copy.ts); `precautions`
 * only adds movement-specific ease-off notes.
 *
 * `contraindicationTags` are intensity flags ("strong-flexion"), used by the
 * coaching prompt to avoid recommending the hardest variations by default.
 *
 * Archive policy: an exercise dropped from the library moves to
 * ARCHIVED_EXERCISES unchanged, never deleted — old logs and recommendations
 * still name it by id. Archived entries are never shown in the library and
 * never recommended (EXERCISE_IDS lists active ids only).
 */
export const EXERCISES = [
    // ── Cervical ──────────────────────────────────────────────────────────────
    {
        id: 'chin-tuck',
        name: 'Seated chin tuck',
        region: 'cervical',
        position: 'seated',
        tags: ['fhp'],
        difficulty: 1,
        durationSec: 60,
        reps: '10 reps, 3 s hold',
        cues: [
            'Sit tall and look straight ahead.',
            'Glide your chin straight back, like making a double chin.',
            'Hold three seconds, then ease back to neutral.',
        ],
        steps: [
            'Sit toward the front of your chair with your feet flat and your shoulders relaxed down.',
            'Pick a point straight ahead at eye level and keep your eyes on it.',
            'Glide your head straight back, eyes level, until you feel a gentle stretch at the base of the skull.',
            'Hold for three seconds without shrugging.',
            'Let your head return slowly to neutral — not past it — and repeat. Breathe normally through each hold.',
        ],
        mistakes: [
            'Keep your eyes level — slide straight back, don’t nod down.',
            'Let the shoulders stay soft; the movement is in the neck, not a shrug.',
            'Go only as far as is comfortable rather than pushing to a hard end point.',
        ],
        precautions: [
            'Keep it to a gentle stretch; ease off if you feel tingling into the arms or it gets sharper.',
        ],
        voiceScript: 'Sit tall toward the front of your chair, feet flat on the floor. Let your shoulders drop. ' +
            'Look at a spot straight ahead. Now glide your chin straight back, as if making a double chin, ' +
            'eyes staying level. Hold… two… three. And slowly let it go. That’s one. ' +
            'Again: glide back… hold… two… three. Release. Keep breathing easily. ' +
            'Carry on at your own pace, ten in all, three seconds each time. ' +
            'Last one: glide back… hold… two… three. And relax. Let your head settle back where it started.',
        media: [],
        contraindicationTags: [],
        illustration: 'chin-tuck',
    },
    {
        id: 'chin-tuck-supine',
        name: 'Lying chin tuck',
        region: 'cervical',
        position: 'supine',
        tags: ['fhp'],
        difficulty: 1,
        durationSec: 90,
        reps: '10 reps, 5 s hold',
        cues: [
            'Lie on your back, knees bent, head resting on the floor.',
            'Draw your chin in so the back of your head slides along the floor.',
            'Hold five seconds, then relax completely.',
        ],
        steps: [
            'Lie on your back on a mat or carpet with your knees bent and feet flat. A thin folded towel under your head is fine if your chin tips up.',
            'Rest your arms by your sides and let your jaw relax: lips together, teeth slightly apart.',
            'Gently draw your chin in so the back of your head glides along the floor toward the top of the mat.',
            'Hold for five seconds with your head still resting on the floor — no lifting.',
            'Relax fully back to the start, take a breath, and repeat.',
        ],
        mistakes: [
            'Keep your head resting on the floor — slide it, don’t lift it.',
            'Keep the jaw loose instead of clenching your teeth.',
            'Use a small, quiet movement rather than pressing your head down hard.',
        ],
        precautions: [
            'Skip lying positions if getting down to the floor is hard; the seated chin tuck works the same movement.',
            'Ease off if you feel tingling into the arms or it gets sharper.',
        ],
        voiceScript: 'Lie on your back with your knees bent and your feet flat. Let your arms rest by your sides. ' +
            'Relax your jaw — lips together, teeth slightly apart. ' +
            'Now gently draw your chin in, letting the back of your head slide along the floor. ' +
            'Keep your head down; this is a slide, not a lift. Hold… two… three… four… five. And relax. Breathe in. ' +
            'Again: draw the chin in… hold… two… three… four… five. Release. ' +
            'Carry on for ten slow repetitions. ' +
            'Last one: draw in… hold… and let go. Rest your head and breathe.',
        media: [],
        contraindicationTags: [],
        illustration: 'chin-tuck',
    },
    {
        id: 'chin-tuck-towel',
        name: 'Chin tuck with towel',
        region: 'cervical',
        position: 'seated',
        tags: ['fhp'],
        difficulty: 2,
        durationSec: 90,
        reps: '10 reps, 5 s hold',
        cues: [
            'Loop a towel behind your neck and hold both ends in front.',
            'Glide your chin straight back into the towel.',
            'Hold five seconds with the towel steady, then release.',
        ],
        steps: [
            'Sit tall with your feet flat. Roll a hand towel lengthwise and place it behind your neck, just below the base of the skull.',
            'Hold one end in each hand in front of you, elbows relaxed, with light tension in the towel.',
            'Glide your chin straight back, pressing the back of your neck gently into the towel — it gives you a guide and something light to push against.',
            'Hold for five seconds with your eyes level.',
            'Release slowly to neutral and repeat. Breathe out as you glide back, breathe in as you release.',
        ],
        mistakes: [
            'Keep the pull on the towel light — it guides the movement, it doesn’t pull the head forward.',
            'Keep your eyes level instead of tipping the chin up or down.',
            'Let the shoulders stay low rather than lifting toward the ears.',
        ],
        precautions: [
            'Ease off the towel tension if the neck feels strained; the plain seated chin tuck is the easier step.',
            'Stop if you feel tingling into the arms.',
        ],
        voiceScript: 'Sit tall with your feet flat. Place the rolled towel behind your neck, just under the base of your skull, ' +
            'and hold one end in each hand in front of you. Keep a little tension in it. ' +
            'Breathe out and glide your chin straight back into the towel, eyes level. ' +
            'Hold… two… three… four… five. And slowly release. ' +
            'Again: glide back into the towel… hold… two… three… four… five. Release. ' +
            'Keep your shoulders low and your grip light. Carry on for ten in total. ' +
            'Last one: glide back… hold… and release. Lower the towel and let your head rest in neutral.',
        media: [],
        contraindicationTags: [],
        illustration: 'chin-tuck',
    },
    {
        id: 'shoulder-rolls',
        name: 'Shoulder circles',
        region: 'cervical',
        position: 'seated',
        tags: ['general', 'scap'],
        difficulty: 1,
        durationSec: 45,
        reps: '10 circles each direction',
        cues: [
            'Lift the shoulders up toward the ears.',
            'Roll them back and down in a slow circle.',
            'Keep the neck long and relaxed.',
        ],
        steps: [
            'Sit or stand tall with your arms hanging loosely at your sides.',
            'Lift both shoulders up toward your ears.',
            'Draw them back, letting the shoulder blades come gently together.',
            'Lower them down, then let them come forward to finish the circle.',
            'Do ten slow backward circles, then ten forward. Breathe in on the way up, out on the way down.',
        ],
        mistakes: [
            'Move slowly and smoothly rather than rushing the circles.',
            'Keep your head still and your chin level — let the shoulders do the work.',
        ],
        precautions: ['Make the circles smaller if anything pinches at the top of the shoulder.'],
        voiceScript: 'Sit tall and let your arms hang loosely by your sides. ' +
            'Breathe in and lift both shoulders up toward your ears. ' +
            'Roll them back… and breathe out as they drop down… and forward. That’s one circle. ' +
            'Again: up… back… down… forward. Keep your head still and your chin level. ' +
            'Make the circles as big as feels easy, and keep them slow. ' +
            'Ten backward circles in all. Now reverse: up… forward… down… back. ' +
            'Ten this way too. Last one: up… around… and down. Let your shoulders rest.',
        media: [],
        contraindicationTags: [],
        illustration: 'scap-retract',
    },
    {
        id: 'upper-trap-stretch',
        name: 'Upper trap stretch',
        region: 'cervical',
        position: 'seated',
        tags: ['fhp', 'asymmetry'],
        difficulty: 1,
        durationSec: 150,
        reps: '30 s each side, 2 rounds',
        cues: [
            'Hold the chair seat with one hand to anchor that shoulder.',
            'Tip your ear toward the opposite shoulder.',
            'Rest the free hand on your head — weight, never a pull.',
        ],
        steps: [
            'Sit tall and hold the edge of your chair seat with your right hand to keep that shoulder down.',
            'Keep your nose pointing forward and tip your left ear toward your left shoulder.',
            'Rest your left hand lightly on the right side of your head to add a little weight.',
            'Hold for thirty seconds; you should feel the stretch along the right side of the neck and the top of the shoulder.',
            'Take the hand away, bring your head upright slowly, and switch sides. Breathe slowly and let each exhale soften the stretch.',
        ],
        mistakes: [
            'Keep your nose facing forward — don’t let the head turn.',
            'Keep the anchored shoulder down instead of letting it ride up.',
            'Let the hand rest — don’t pull the head down.',
        ],
        precautions: [
            'Keep the stretch gentle; ease off if you feel tingling down the arm or it gets sharper.',
        ],
        voiceScript: 'Sit tall and hold the edge of your chair with your right hand. ' +
            'Keep your nose pointing forward and slowly tip your left ear toward your left shoulder. ' +
            'Rest your left hand on your head, just for weight. ' +
            'Feel the stretch along the right side of your neck. Breathe in… and out. ' +
            'Hold… ten… twenty… thirty. ' +
            'Take your hand away and lift your head slowly to upright. ' +
            'Now the other side: hold the chair with your left hand, tip your right ear toward your right shoulder, ' +
            'rest the hand. Hold… ten… twenty… thirty. And slowly come back to centre.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'levator-stretch',
        name: 'Levator scapulae stretch',
        region: 'cervical',
        position: 'seated',
        tags: ['fhp', 'asymmetry'],
        difficulty: 1,
        durationSec: 150,
        reps: '30 s each side, 2 rounds',
        cues: [
            'Anchor one hand on the chair seat.',
            'Turn your nose toward the opposite armpit and look down.',
            'Rest the other hand on your head and breathe into the stretch.',
        ],
        steps: [
            'Sit tall and hold the edge of your chair seat with your right hand.',
            'Turn your head about 45° to the left, as if looking toward your left pocket.',
            'Lower your chin toward your left armpit.',
            'Rest your left hand on the back of your head for light weight and hold thirty seconds — the stretch sits at the back of the right side of the neck, toward the shoulder blade.',
            'Lift your head out slowly and switch sides. Breathe slowly throughout.',
        ],
        mistakes: [
            'Keep the anchored shoulder low rather than shrugging.',
            'Let the hand add weight only — no pulling.',
            'Keep your upper back tall instead of slumping into the stretch.',
        ],
        precautions: [
            'Keep it gentle; ease off if you feel tingling down the arm or it gets sharper.',
        ],
        voiceScript: 'Sit tall and hold the edge of the chair with your right hand. ' +
            'Turn your head about halfway to the left, then lower your chin toward your left armpit. ' +
            'Rest your left hand on the back of your head, just for weight. ' +
            'Feel the stretch at the back of your neck on the right. Breathe out and soften. ' +
            'Hold… ten… twenty… thirty. ' +
            'Lift your head slowly and come back to centre. ' +
            'Now switch: left hand on the chair, turn right, chin toward your right armpit, hand resting on your head. ' +
            'Hold… ten… twenty… thirty. And lift slowly back to upright.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'scalene-stretch',
        name: 'Scalene stretch',
        region: 'cervical',
        position: 'seated',
        tags: ['fhp', 'asymmetry'],
        difficulty: 1,
        durationSec: 150,
        reps: '30 s each side, 2 rounds',
        cues: [
            'Hold your collarbone down with one hand.',
            'Tip your ear away, then lift the chin a little.',
            'Breathe slowly into the lower ribs.',
        ],
        steps: [
            'Sit tall. Place your right hand flat on your upper chest just below the right collarbone to keep it down (or hold the chair seat with that hand).',
            'Tip your left ear toward your left shoulder.',
            'Lift your chin slightly and turn it a touch toward the left, until you feel a stretch along the front and side of the right neck.',
            'Hold thirty seconds, breathing slowly into the lower ribs.',
            'Come back to upright slowly and switch sides.',
        ],
        mistakes: [
            'Keep the chin lift small — don’t tip the head far back.',
            'Keep the anchored collarbone and shoulder down.',
            'Breathe low and slow instead of holding your breath.',
        ],
        precautions: [
            'Keep it gentle; ease off if you feel tingling into the arm or hand.',
            'Come back to upright and rest if you feel lightheaded.',
        ],
        voiceScript: 'Sit tall. Place your right hand flat just below your right collarbone and let it hold that spot down. ' +
            'Slowly tip your left ear toward your left shoulder. ' +
            'Now lift your chin just a little, and turn it slightly to the left. ' +
            'Feel the stretch along the front and side of your neck. ' +
            'Breathe into your lower ribs. Hold… ten… twenty… thirty. ' +
            'Slowly come back to upright. ' +
            'Switch: left hand below your left collarbone, right ear toward your right shoulder, small chin lift. ' +
            'Hold… ten… twenty… thirty. And come back to centre.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'scm-stretch',
        name: 'SCM stretch',
        region: 'cervical',
        position: 'seated',
        tags: ['fhp', 'asymmetry'],
        difficulty: 2,
        durationSec: 150,
        reps: '20–30 s each side, 2 rounds',
        cues: [
            'Rest both hands on one collarbone.',
            'Tip your head away, then turn your face back toward that side.',
            'Lift the chin only slightly; keep it gentle.',
        ],
        steps: [
            'Sit tall and rest both hands, one over the other, on your right collarbone to keep it down.',
            'Tip your left ear toward your left shoulder.',
            'Turn your face slowly back toward the right and lift your chin a little, until you feel a stretch along the front of the right neck, from behind the ear down to the collarbone.',
            'Hold twenty to thirty seconds, breathing slowly.',
            'Return to upright in the same order — turn back, then lift the ear — and switch sides.',
        ],
        mistakes: [
            'Keep the chin lift small — this is not a strong look-up.',
            'Let the hands rest on the collarbone rather than pressing hard.',
            'Move slowly into the stretch instead of bouncing.',
        ],
        precautions: [
            'Keep this one light: no strong backward bending of the neck.',
            'Come back to upright and rest if you feel lightheaded, or stop if you feel tingling into the arm.',
        ],
        voiceScript: 'Sit tall. Rest both hands on your right collarbone, one over the other. ' +
            'Slowly tip your left ear toward your left shoulder. ' +
            'Now turn your face back toward the right, and lift your chin just a little. ' +
            'Feel the stretch along the front of your neck on the right. Keep it gentle. ' +
            'Breathe in… and out. Hold… ten… twenty. ' +
            'Turn back to centre, then lift your head upright. ' +
            'Switch: hands on your left collarbone, right ear toward your right shoulder, turn your face to the left, small chin lift. ' +
            'Hold… ten… twenty. And slowly come back to upright.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'deep-neck-flexor',
        name: 'Lying deep neck flexor hold',
        region: 'cervical',
        position: 'supine',
        tags: ['fhp'],
        difficulty: 2,
        durationSec: 120,
        reps: '8 reps, 5 s hold',
        cues: [
            'Lie on your back and tuck your chin in first.',
            'Keep the tuck and lift your head a centimetre off the floor.',
            'Hold five seconds; lower before the neck starts to shake.',
        ],
        steps: [
            'Lie on your back on a mat with your knees bent and feet flat, arms by your sides.',
            'Start with a lying chin tuck: nod slightly so the back of your head slides along the floor.',
            'Keeping that tuck, lift your head about a centimetre off the floor.',
            'Hold for five seconds with your jaw loose, breathing steadily.',
            'Lower your head gently, release the tuck, and rest a breath before the next one.',
        ],
        mistakes: [
            'Keep the chin tucked — don’t let it poke up toward the ceiling as you lift.',
            'Lift only a centimetre; a higher lift lets the bigger neck muscles take over.',
            'Keep breathing instead of holding your breath.',
        ],
        precautions: [
            'Skip lying positions if getting down to the floor is hard; use the seated chin tuck instead.',
            'Lower your head as soon as the front of the neck starts to shake or the chin starts to poke; start with the lying chin tuck if the hold is hard.',
        ],
        voiceScript: 'Lie on your back with your knees bent and your arms by your sides. ' +
            'First, a small nod: tuck your chin in so the back of your head slides along the floor. ' +
            'Keep that tuck, and lift your head just a centimetre off the floor. ' +
            'Hold… two… three… four… five. Lower your head gently. Release, and breathe. ' +
            'Again: tuck… lift a little… hold… two… three… four… five. And lower. ' +
            'Keep your jaw loose. Eight in total; stop early if your neck starts to shake. ' +
            'Last one: tuck… lift… hold… and lower. Let your head rest.',
        media: [],
        contraindicationTags: ['strong-flexion'],
        illustration: 'core',
    },
    {
        id: 'neck-range',
        name: 'Neck range of motion',
        region: 'cervical',
        position: 'seated',
        tags: ['general', 'fhp'],
        difficulty: 1,
        durationSec: 120,
        reps: '5 slow reps each direction',
        cues: [
            'Move slowly through all four directions.',
            'Stay in an easy range; never force the end point.',
            'Return to centre between each movement.',
        ],
        steps: [
            'Sit tall with your shoulders relaxed and your eyes forward.',
            'Forward and back: lower your chin toward your chest, return to centre, then look gently up toward the ceiling only as far as is comfortable.',
            'Turning: look over your right shoulder, back to centre, then over your left.',
            'Side bending: tip your right ear toward your right shoulder, back to centre, then the left.',
            'Pause for a breath at the end of each movement and come back through centre every time.',
        ],
        mistakes: [
            'Keep the shoulders still — move the head, not the trunk.',
            'Keep the look-up small and easy rather than dropping the head back.',
            'Move smoothly instead of bouncing at the end.',
        ],
        precautions: [
            'Keep the look-up small; stop and sit for a moment if you feel lightheaded.',
            'Ease off any direction that brings tingling into the arms.',
        ],
        voiceScript: 'Sit tall, shoulders relaxed, eyes forward. ' +
            'Slowly lower your chin toward your chest… and back to centre. Now look gently up… and back to centre. ' +
            'Repeat that five times at an easy pace. ' +
            'Now turn: look over your right shoulder… centre… over your left shoulder… centre. Five each way. ' +
            'Now tip your right ear toward your right shoulder… centre… left ear toward your left shoulder… centre. Five each way. ' +
            'Breathe through every movement and stay in an easy range. ' +
            'Finish back in the middle, eyes forward, shoulders soft.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    // ── Thoracic: stretches ───────────────────────────────────────────────────
    {
        id: 'thread-the-needle',
        name: 'Thread the needle',
        region: 'thoracic',
        position: 'quadruped',
        tags: ['thoracic-ext', 'asymmetry'],
        difficulty: 2,
        durationSec: 120,
        reps: '6 reps each side, 3 s hold',
        cues: [
            'From hands and knees, slide one arm under the other.',
            'Let the shoulder and side of the head rest down.',
            'Open back up and reach that arm toward the ceiling.',
        ],
        steps: [
            'Start on hands and knees on a mat or carpet: hands under shoulders, knees under hips.',
            'Lift your right hand and slide it, palm up, under your left arm along the floor.',
            'Let your right shoulder and the side of your head lower toward the floor; hold three seconds and breathe out.',
            'Unthread and reach your right arm up toward the ceiling, following your hand with your eyes.',
            'Do six on this side, then switch.',
        ],
        mistakes: [
            'Keep your hips stacked over your knees instead of sitting back or sliding sideways.',
            'Turn through the upper back rather than just reaching with the arm.',
            'Rest the head down gently — don’t push weight into the neck.',
        ],
        precautions: [
            'Put a folded towel under your knees if kneeling is uncomfortable.',
            'Skip it if getting down to the floor is hard; the seated thoracic extension is a chair option.',
            'Keep the turn easy; ease off if the shoulder pinches.',
        ],
        voiceScript: 'Come onto your hands and knees, hands under your shoulders, knees under your hips. ' +
            'Lift your right hand and slide it under your left arm, palm up. ' +
            'Let your right shoulder and the side of your head lower toward the floor. Breathe out. Hold… two… three. ' +
            'Now unthread and reach your right arm up toward the ceiling, eyes following your hand. ' +
            'And back under… hold… two… three. Up again. ' +
            'Keep your hips over your knees. Six on this side. ' +
            'Now switch to the left arm and do six more. ' +
            'Finish by placing both hands back on the floor, back flat.',
        media: [],
        contraindicationTags: [],
        illustration: 'thoracic-ext',
    },
    {
        id: 'open-book',
        name: 'Open book',
        region: 'thoracic',
        position: 'side-lying',
        tags: ['thoracic-ext', 'asymmetry'],
        difficulty: 1,
        durationSec: 150,
        reps: '8 reps each side, 3 s hold',
        cues: [
            'Lie on your side, knees bent and together.',
            'Open the top arm up and over, eyes following the hand.',
            'Keep the knees down; turn from the upper back.',
        ],
        steps: [
            'Lie on your left side with your hips and knees bent to about 90°, head resting on a folded towel or pillow.',
            'Stretch both arms out in front of you at shoulder height, palms together.',
            'Keeping your knees together on the floor, lift your right arm up and over, opening your chest toward the ceiling, and let your eyes follow your hand.',
            'Open only as far as is comfortable — the hand doesn’t need to touch the floor — and hold three seconds, breathing out.',
            'Bring the arm back to meet the other hand. Repeat eight times, then turn over and do the other side.',
        ],
        mistakes: [
            'Keep the knees stacked together — let the turn come from the upper back, not the hips.',
            'Let your head turn with the arm instead of holding it still.',
            'Open the arm slowly rather than flinging it.',
        ],
        precautions: [
            'Put a pillow under the opening arm if it hangs uncomfortably; ease off if the front of the shoulder pinches.',
            'Skip it if lying on your side or getting down to the floor is hard; the seated thoracic extension is a chair option.',
        ],
        voiceScript: 'Lie on your left side, knees bent and together, head resting on a pillow or towel. ' +
            'Stretch both arms out in front of you, palms together. ' +
            'Breathe in. As you breathe out, lift your top arm up and over, opening your chest toward the ceiling. Follow your hand with your eyes. ' +
            'Keep your knees together. Hold… two… three. ' +
            'And bring the arm back to meet the other hand. ' +
            'Again: open… hold… two… three… and back. ' +
            'Eight on this side. Then roll over onto your right side and do eight more. ' +
            'Finish with both hands together, resting.',
        media: [],
        contraindicationTags: [],
        illustration: 'thoracic-ext',
    },
    {
        id: 'seated-thoracic-extension',
        name: 'Seated thoracic extension',
        region: 'thoracic',
        position: 'seated',
        tags: ['thoracic-ext'],
        difficulty: 1,
        durationSec: 75,
        reps: '10 reps, 3 s hold',
        cues: [
            'Sit back so the chair top meets your upper back.',
            'Hands behind your head, elbows forward.',
            'Lean back over the chair edge, then return.',
        ],
        steps: [
            'Sit in a sturdy chair with a firm back that reaches to about the middle of your shoulder blades. Scoot back so your upper back rests against the top edge.',
            'Rest your hands lightly behind your head with your elbows pointing forward, or cross your arms over your chest.',
            'Breathe in. As you breathe out, gently lean your upper back over the top of the chair, letting your chest lift toward the ceiling.',
            'Keep your hips and lower back still on the seat and hold for three seconds.',
            'Return to upright and repeat ten times.',
        ],
        mistakes: [
            'Bend from the upper back — keep the ribs from flaring and the lower back from arching.',
            'Keep your head in line with your trunk rather than dropping it back.',
            'Keep the hands light and elbows forward — don’t pull on your neck.',
        ],
        precautions: [
            'Use a chair without wheels, or set it against a wall so it can’t tip.',
            'Keep the movement small; ease off if you feel a pinch in the back.',
        ],
        voiceScript: 'Sit back in your chair so the top edge meets your upper back. ' +
            'Rest your hands lightly behind your head, elbows pointing forward. ' +
            'Breathe in. As you breathe out, lean your upper back gently over the chair, chest lifting toward the ceiling. ' +
            'Hold… two… three. And come back up. ' +
            'Again: breathe in… breathe out and lean back… hold… two… three. Up. ' +
            'Keep your hips still and your head in line with your body. ' +
            'Ten in total, at an easy pace. ' +
            'Last one: lean back… hold… and return. Sit tall and lower your arms.',
        media: [],
        contraindicationTags: [],
        illustration: 'thoracic-ext',
    },
    {
        id: 'lat-stretch',
        name: 'Lat stretch',
        region: 'thoracic',
        position: 'standing',
        tags: ['thoracic-ext'],
        difficulty: 1,
        durationSec: 120,
        reps: '30 s, 3 rounds',
        cues: [
            'Hands on the desk edge, feet stepped back.',
            'Push your hips back and let your chest sink between your arms.',
            'Keep your head between your arms and breathe into your sides.',
        ],
        steps: [
            'Stand facing a steady desk or a wall, about a step and a half away. Place both hands on the edge, shoulder-width apart.',
            'Walk your feet back and push your hips back, letting your chest sink between your arms until your arms and back make one long line.',
            'Keep a soft bend in the knees and your ears in line with your arms.',
            'Hold for thirty seconds, breathing into the sides of your ribs; you should feel the stretch along the sides of your back, under the arms.',
            'Walk your feet back in and stand up slowly.',
        ],
        mistakes: [
            'Keep the lower back long — don’t let it sag into an arch.',
            'Keep your head in line with your arms instead of dropping or lifting it.',
            'Bend the knees as much as you need rather than forcing the hips back.',
        ],
        precautions: [
            'Make sure the desk is steady and won’t slide.',
            'Ease off if the shoulders pinch; place your hands lower or step a little closer.',
        ],
        voiceScript: 'Stand facing your desk and place both hands on the edge, shoulder-width apart. ' +
            'Walk your feet back and push your hips back. Let your chest sink down between your arms. ' +
            'Soft knees. Head between your arms. ' +
            'Breathe into the sides of your ribs. Hold… ten… twenty… thirty. ' +
            'Walk your feet in a little and stand up slowly. Rest a breath. ' +
            'Again: hands on the desk, hips back, chest sinking. Hold… ten… twenty… thirty. ' +
            'One more round when you’re ready. ' +
            'To finish, walk your feet in and roll up to standing.',
        media: [],
        contraindicationTags: ['overhead'],
        illustration: 'stretch',
    },
    // ── Thoracic: strengthening ───────────────────────────────────────────────
    {
        id: 'scapular-retraction',
        name: 'Seated scapular retraction hold',
        region: 'thoracic',
        position: 'seated',
        tags: ['scap'],
        difficulty: 1,
        durationSec: 90,
        reps: '10 reps, 5 s hold',
        cues: [
            'Sit tall with your arms relaxed.',
            'Draw the shoulder blades back and slightly down.',
            'Hold five seconds without shrugging or arching.',
        ],
        steps: [
            'Sit tall toward the front of your chair, feet flat, arms relaxed by your sides.',
            'Draw your shoulder blades back and slightly down, as if tucking them into your back pockets.',
            'Hold for five seconds; you should feel the muscles between the shoulder blades working.',
            'Release slowly to relaxed — not all the way to slumped.',
            'Breathe normally through each hold and repeat ten times.',
        ],
        mistakes: [
            'Keep the shoulders down away from the ears — no shrug.',
            'Keep the ribs down; don’t arch the lower back to make the squeeze look bigger.',
            'Keep your chin level rather than pushing the head forward.',
        ],
        precautions: ['Use a moderate squeeze; ease off if anything cramps or pinches.'],
        voiceScript: 'Sit tall toward the front of your chair, feet flat, arms relaxed by your sides. ' +
            'Now draw your shoulder blades back and a little down, as if tucking them into your back pockets. ' +
            'Hold… two… three… four… five. And release slowly. ' +
            'Again: back and down… hold… two… three… four… five. Release. ' +
            'Keep your shoulders away from your ears and your ribs soft. Keep breathing. ' +
            'Ten in total. ' +
            'Last one: back and down… hold… two… three… four… five. And relax, sitting tall.',
        media: [],
        contraindicationTags: [],
        illustration: 'scap-retract',
    },
    {
        id: 'standing-w-retraction',
        name: 'Standing W hold',
        region: 'thoracic',
        position: 'standing',
        tags: ['scap', 'thoracic-ext'],
        difficulty: 1,
        durationSec: 90,
        reps: '8 reps, 5 s hold',
        cues: [
            'Elbows bent and low, hands up at shoulder height — a W.',
            'Squeeze the elbows back and down.',
            'Hold five seconds with the chin gently tucked.',
        ],
        steps: [
            'Stand tall with feet hip-width apart and knees soft.',
            'Bend your elbows and lift your hands to about shoulder height, palms facing forward, elbows down by your ribs — your arms make a W.',
            'Draw the elbows back and down, bringing the shoulder blades toward each other, and turn the thumbs slightly outward.',
            'Hold for five seconds with your chin gently tucked.',
            'Relax the arms and repeat. Breathe out as you squeeze, in as you relax.',
        ],
        mistakes: [
            'Keep the shoulders away from your ears.',
            'Keep the ribs down — don’t lean back to move the elbows further.',
            'Keep your chin level rather than letting it poke forward.',
        ],
        precautions: [
            'Keep the hands lower if lifting them pinches the shoulders; ease off if you feel tingling into the hands.',
        ],
        voiceScript: 'Stand tall, feet hip-width apart, knees soft. ' +
            'Bend your elbows and bring your hands up to shoulder height, palms forward, elbows low. Your arms make a W. ' +
            'Breathe out and squeeze your elbows back and down, shoulder blades drawing together. ' +
            'Tuck your chin gently. Hold… two… three… four… five. And relax. ' +
            'Again: squeeze back and down… hold… two… three… four… five. Relax. ' +
            'Keep your ribs down and your shoulders low. Eight in total. ' +
            'Last one: squeeze… hold… and let your arms rest by your sides.',
        media: [],
        contraindicationTags: [],
        illustration: 'scap-retract',
    },
    {
        id: 'wall-angel',
        name: 'Wall angel',
        region: 'thoracic',
        position: 'standing',
        tags: ['fhp', 'scap', 'thoracic-ext'],
        difficulty: 2,
        durationSec: 90,
        reps: '8 slow reps',
        cues: [
            'Back and head against a wall, feet a little forward.',
            'Arms on the wall in a goalpost shape.',
            'Slide up and down, keeping contact the whole way.',
        ],
        steps: [
            'Stand with your back to a wall, feet about 30 cm (a foot) forward, knees soft.',
            'Let your hips, upper back and — if comfortable — the back of your head rest on the wall, with a light chin tuck.',
            'Bring your arms to the wall in a goalpost shape: elbows at shoulder height, bent to 90°.',
            'Slowly slide your arms up as far as you can while keeping contact, then slide back down to the goalpost.',
            'Breathe out as you slide up, in as you come down.',
        ],
        mistakes: [
            'Keep the lower ribs down — don’t arch your lower back away from the wall to reach higher.',
            'Stop the slide where contact is lost instead of forcing the arms up.',
            'Keep your head level against the wall rather than tipping the chin up.',
        ],
        precautions: [
            'If your head can’t touch the wall without tipping the chin up, put a folded towel behind it.',
            'Keep the range smaller if the shoulders pinch; ease off if the hands tingle.',
        ],
        voiceScript: 'Stand with your back against a wall, feet a little forward, knees soft. ' +
            'Let your hips, upper back and head rest on the wall. Tuck your chin lightly. ' +
            'Bring your arms up into a goalpost, backs of the hands toward the wall. ' +
            'Breathe out and slowly slide your arms up… only as far as they stay in contact. ' +
            'Breathe in and slide back down to the goalpost. ' +
            'Again: up… and down. Keep your ribs down and your lower back near the wall. ' +
            'Eight slow ones in total. ' +
            'Last one: up… and down. Lower your arms and step away from the wall.',
        media: [],
        contraindicationTags: ['overhead'],
        illustration: 'wall-angel',
    },
    {
        id: 'seated-lat-activation',
        name: 'Seated lat activation',
        region: 'thoracic',
        position: 'seated',
        tags: ['scap'],
        difficulty: 1,
        durationSec: 75,
        reps: '10 reps, 5 s hold',
        cues: [
            'Hands flat on the seat beside your hips.',
            'Press down and draw the shoulders away from your ears.',
            'Hold five seconds, sitting tall.',
        ],
        steps: [
            'Sit tall in a sturdy chair with your hands flat on the seat beside your hips (or on the armrests), elbows straight but not locked.',
            'Keeping your trunk tall, press your palms down and draw your shoulders down, away from your ears, and slightly back.',
            'Feel the sides of your back under the arms working; your body may lift a touch off the seat.',
            'Hold for five seconds, then let the shoulders relax.',
            'Breathe out as you press, in as you release. Repeat ten times.',
        ],
        mistakes: [
            'Press straight down — don’t lean or hunch forward over your hands.',
            'Keep the neck long and relaxed rather than tensing it.',
            'Keep the elbows soft instead of locking them.',
        ],
        precautions: [
            'Use a chair without wheels.',
            'Press with moderate effort; if your wrists feel strained, rest on your fists instead of flat palms.',
        ],
        voiceScript: 'Sit tall and place your hands flat on the seat beside your hips, elbows straight but soft. ' +
            'Breathe out and press your palms down. Draw your shoulders down, away from your ears. ' +
            'Feel the sides of your back switch on. Hold… two… three… four… five. And relax. ' +
            'Again: press down… shoulders down… hold… two… three… four… five. Relax. ' +
            'Stay tall; no leaning forward. ' +
            'Ten in total. ' +
            'Last one: press… hold… two… three… four… five. And let your hands rest on your thighs.',
        media: [],
        contraindicationTags: [],
        illustration: 'scap-retract',
    },
    {
        id: 'standing-y',
        name: 'Standing Y raise',
        region: 'thoracic',
        position: 'standing',
        tags: ['scap', 'thoracic-ext'],
        difficulty: 2,
        durationSec: 90,
        reps: '10 reps, 2 s hold',
        cues: [
            'Thumbs up, shoulder blades set back and down.',
            'Raise both arms up and out into a Y.',
            'Pause at the top, lower slowly.',
        ],
        steps: [
            'Stand tall, feet hip-width apart, knees soft, arms by your sides with thumbs pointing forward.',
            'Set your shoulder blades gently back and down and lightly tuck your chin.',
            'Raise both arms forward and up into a Y shape, a little wider than your head, thumbs leading toward the ceiling.',
            'Pause for two seconds at the top, keeping the ribs down.',
            'Lower slowly over about three seconds. Breathe out as you lift, in as you lower.',
        ],
        mistakes: [
            'Keep the ribs down — don’t arch the lower back to get the arms higher.',
            'Keep the shoulders out of your ears at the top.',
            'Lower slowly instead of letting the arms drop.',
        ],
        precautions: ['Lift only as high as stays comfortable; ease off if the shoulders pinch.'],
        voiceScript: 'Stand tall, feet hip-width apart, arms by your sides with your thumbs pointing forward. ' +
            'Set your shoulder blades back and down and tuck your chin lightly. ' +
            'Breathe out and raise both arms up and out into a Y, thumbs leading. ' +
            'Pause at the top… two. Now lower slowly… two… three. ' +
            'Again: lift into the Y… pause… two. Lower… two… three. ' +
            'Keep your ribs down and your shoulders away from your ears. ' +
            'Ten in total. ' +
            'Last one: lift… pause… and lower all the way. Let your arms hang and relax.',
        media: [],
        contraindicationTags: ['overhead'],
        illustration: 'wall-angel',
    },
    {
        id: 'prone-ity',
        name: 'Lying I, T, Y',
        region: 'thoracic',
        position: 'prone',
        tags: ['scap', 'thoracic-ext'],
        difficulty: 3,
        durationSec: 180,
        reps: '6 reps each letter, 2 s hold',
        cues: [
            'Lie face down, forehead on a folded towel.',
            'Lift the arms a few centimetres in an I, then a T, then a Y.',
            'Lead with the shoulder blades, not the hands.',
        ],
        steps: [
            'Lie face down on a mat with a folded towel under your forehead and your legs long.',
            'I: arms by your sides, palms toward your body. Draw the shoulder blades back and lift your hands a few centimetres; hold two seconds and lower.',
            'T: arms straight out to the sides, thumbs up. Squeeze the shoulder blades together to lift the arms just off the floor; hold and lower.',
            'Y: arms overhead in a Y, thumbs up. Lift a few centimetres from the shoulder blades; hold and lower.',
            'Keep your forehead on the towel and breathe out on each lift.',
        ],
        mistakes: [
            'Lead with the shoulder blades — small lifts are fine; don’t swing the arms.',
            'Keep your forehead resting down rather than lifting the head to look forward.',
            'Keep the lower back quiet — don’t arch to lift higher.',
        ],
        precautions: [
            'Skip lying positions if getting down to the floor is hard; the standing W hold and standing Y raise are the upright versions.',
            'Leave out the Y if raising the arms overhead pinches the shoulders.',
        ],
        voiceScript: 'Lie face down with your forehead resting on a folded towel, legs long. ' +
            'Start with the I: arms by your sides. Draw your shoulder blades back and lift your hands a little. Hold… two. Lower. Six of those. ' +
            'Now the T: arms straight out to the sides, thumbs up. Squeeze your shoulder blades and lift. Hold… two. Lower. Six. ' +
            'Now the Y: arms overhead in a Y, thumbs up. Lift from the shoulder blades. Hold… two. Lower. Six. ' +
            'Keep your forehead down the whole time. ' +
            'Finish by resting your arms by your sides and breathing.',
        media: [],
        contraindicationTags: ['overhead'],
        illustration: 'scap-retract',
    },
    // ── Posture ──────────────────────────────────────────────────────────────
    {
        id: 'upright-hold',
        name: 'Upright posture hold',
        region: 'posture',
        position: 'seated',
        tags: ['fhp', 'scap'],
        difficulty: 1,
        durationSec: 120,
        reps: '5 holds of 20 s',
        cues: [
            'Sit on top of your sitting bones, feet flat.',
            'Add a gentle chin tuck and draw the shoulder blades back.',
            'Hold at an easy effort and keep breathing.',
        ],
        steps: [
            'Sit toward the front of your chair with your feet flat and knees about level with your hips.',
            'Find the top of your sitting bones: rock the pelvis forward and back a few times, then settle in the middle.',
            'Grow tall through the crown of your head, add a gentle chin tuck, and draw the shoulder blades back and down.',
            'Hold for twenty seconds at about half effort, breathing slowly and quietly.',
            'Relax for a few breaths without collapsing, then repeat.',
        ],
        mistakes: [
            'Hold it lightly — don’t arch the lower back or puff the chest out.',
            'Keep breathing instead of holding your breath.',
            'Keep your eyes level rather than lifting the chin.',
        ],
        precautions: [
            'This should feel easy to hold; ease off if the back or neck starts to tighten.',
        ],
        voiceScript: 'Sit toward the front of your chair, feet flat. ' +
            'Rock your pelvis gently forward… and back… and settle in the middle, on top of your sitting bones. ' +
            'Now grow tall through the crown of your head. Add a gentle chin tuck. Draw your shoulder blades back and down. ' +
            'Hold it lightly, about half effort. Breathe in… and out. ' +
            'Hold… five… ten… fifteen… twenty. And soften. ' +
            'Rest for a few breaths. ' +
            'Again: tall… chin tuck… shoulder blades back… hold for twenty. ' +
            'Five holds in all. Then let your shoulders relax and sit comfortably.',
        media: [],
        contraindicationTags: [],
        illustration: 'chin-tuck',
    },
    {
        id: 'string-overhead',
        name: 'String overhead',
        region: 'posture',
        position: 'seated',
        tags: ['fhp', 'general'],
        difficulty: 1,
        durationSec: 75,
        reps: '5 reps, 10 s hold',
        cues: [
            'Imagine a string lifting the crown of your head.',
            'Let the back of the neck lengthen as you grow tall.',
            'Keep the shoulders down and the breath low.',
        ],
        steps: [
            'Sit tall (or stand) with your feet flat and arms relaxed.',
            'Imagine a string attached to the crown of your head — the top, toward the back, not your forehead.',
            'Let the string lift you gently upward: the back of the neck lengthens, the chin settles slightly in, and the spine grows tall.',
            'Hold the lift for ten seconds, breathing low into the belly and the sides of the ribs.',
            'Let the string slacken and rest for a breath without slumping, then repeat.',
        ],
        mistakes: [
            'Lift from the back of the crown — don’t tip the chin up.',
            'Keep the shoulders down while you grow tall.',
            'Stay soft through the lower back rather than arching it.',
        ],
        precautions: [
            'Keep it light — it should never feel like straining; ease off if the neck or back starts to tighten.',
            'If you do it standing, stay near the desk so you can steady yourself.',
        ],
        voiceScript: 'Sit tall, feet flat, arms relaxed. ' +
            'Imagine a string attached to the crown of your head, toward the back. ' +
            'Let the string lift you, gently. The back of your neck grows long. Your chin settles a little in. ' +
            'Your shoulders stay down. Breathe low into your belly. ' +
            'Hold… two… three… four… five… six… seven… eight… nine… ten. ' +
            'Let the string go slack, without slumping. Rest a breath. ' +
            'Again: the string lifts… hold for ten. ' +
            'Five times in all. Then let the string go and sit easily.',
        media: [],
        contraindicationTags: [],
        illustration: 'stand-up',
    },
    {
        id: 'bird-dog-chin-tuck',
        name: 'Bird dog with chin tuck',
        region: 'posture',
        position: 'quadruped',
        tags: ['core', 'lbp', 'asymmetry', 'fhp'],
        difficulty: 2,
        durationSec: 150,
        reps: '6 reps each side, 5 s hold',
        cues: [
            'On hands and knees, tuck the chin so the head lines up with the back.',
            'Reach one arm forward and the opposite leg back.',
            'Hold five seconds with the hips level.',
        ],
        steps: [
            'Start on hands and knees on a mat: hands under shoulders, knees under hips, back flat.',
            'Tuck your chin gently so the back of your head lines up with your back; eyes on the floor just ahead of your hands.',
            'Brace your middle lightly, then reach your right arm forward and your left leg back until both are level with your trunk.',
            'Hold for five seconds, keeping the chin tucked and the hips level.',
            'Lower with control and switch sides. Breathe out as you reach.',
        ],
        mistakes: [
            'Keep the hips level — don’t let them twist open as the leg lifts.',
            'Keep the chin tucked instead of lifting the head to look forward.',
            'Reach long rather than high; don’t arch the lower back.',
        ],
        precautions: [
            'Put a folded towel under your knees if kneeling is uncomfortable.',
            'Skip it if getting down to the floor is hard; the upright posture hold is a seated option.',
            'Start with an arm only or a leg only if your balance is shaky.',
        ],
        voiceScript: 'Come onto your hands and knees, hands under your shoulders, knees under your hips, back flat. ' +
            'Tuck your chin gently so your head lines up with your back. ' +
            'Brace your middle a little. Breathe out and reach your right arm forward and your left leg back. ' +
            'Hips level, chin tucked. Hold… two… three… four… five. ' +
            'Lower with control. Now the other side: left arm, right leg. Hold… two… three… four… five. Lower. ' +
            'Six on each side, taking your time. ' +
            'Finish with both hands and both knees on the floor, back flat.',
        media: [],
        contraindicationTags: [],
        illustration: 'core',
    },
    // ── Stretches ─────────────────────────────────────────────────────────────
    {
        id: 'doorway-pec-stretch',
        name: 'Standing pec stretch',
        region: 'stretch',
        position: 'standing',
        tags: ['fhp', 'scap', 'asymmetry'],
        difficulty: 1,
        durationSec: 150,
        reps: '30 s each side, 2 rounds',
        cues: [
            'Forearm on a door frame or wall edge, elbow at shoulder height.',
            'Turn your chest gently away from that arm.',
            'Keep the shoulder back and down, and breathe.',
        ],
        steps: [
            'Stand in a doorway or beside the edge of a wall. Place your right forearm on the frame, elbow at or just below shoulder height.',
            'Step your right foot forward into a small stagger.',
            'Gently turn your chest away from the arm until you feel a stretch across the front of the chest and shoulder.',
            'Keep your shoulder blade drawn back and down and your ribs down; hold for thirty seconds, breathing slowly.',
            'Step back to release and switch arms. For both sides at once, use a wall corner with a forearm on each wall and lean in gently.',
        ],
        mistakes: [
            'Keep the shoulder down and back — don’t let it roll forward into the stretch.',
            'Keep the elbow at or below shoulder height.',
            'Lean in gently instead of pushing to a strong end point.',
        ],
        precautions: [
            'Lower the elbow if you feel tingling into the hand or a pinch at the front of the shoulder.',
        ],
        voiceScript: 'Stand in a doorway and place your right forearm on the frame, elbow at shoulder height. ' +
            'Step your right foot a little forward. ' +
            'Now gently turn your chest away from the arm until you feel a stretch across the front of your chest. ' +
            'Shoulder back and down. Ribs soft. Breathe in… and out. ' +
            'Hold… ten… twenty… thirty. ' +
            'Step back and release. ' +
            'Switch to the left arm: forearm on the frame, left foot forward, turn gently away. ' +
            'Hold… ten… twenty… thirty. ' +
            'Step back, let both arms hang, and roll your shoulders once.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'hip-flexor-stretch',
        name: 'Hip flexor stretch',
        region: 'stretch',
        position: 'standing',
        tags: ['lbp', 'asymmetry'],
        difficulty: 1,
        durationSec: 150,
        reps: '30 s each side, 2 rounds',
        cues: [
            'Long stride, back heel up, a hand on the desk for balance.',
            'Tuck the tailbone under and squeeze the back-leg glute.',
            'Shift slightly forward without arching the back.',
        ],
        steps: [
            'Stand tall with one hand on a desk or wall for balance.',
            'Step your right foot back into a long stride, back heel lifted, both feet pointing forward; bend the front knee slightly.',
            'Tuck your tailbone under and squeeze the right glute until you feel a stretch at the front of the right hip.',
            'Shift your weight a little forward without arching the lower back and hold thirty seconds, breathing slowly.',
            'Step your feet together and switch sides.',
        ],
        mistakes: [
            'Tuck the pelvis first — don’t arch the lower back to lean forward.',
            'Keep the trunk upright rather than leaning over the front leg.',
            'Keep both feet pointing forward.',
        ],
        precautions: [
            'Keep a hand on something steady for balance.',
            'For a deeper version you can kneel with the back knee on a cushion; skip kneeling if it’s uncomfortable on the knee.',
        ],
        voiceScript: 'Stand tall with one hand on your desk. ' +
            'Step your right foot back into a long stride, heel lifted, both feet pointing forward. ' +
            'Tuck your tailbone under and squeeze your right glute. ' +
            'Feel the stretch at the front of your right hip. Shift a little forward, keeping your back long. ' +
            'Breathe in… and out. Hold… ten… twenty… thirty. ' +
            'Step your feet together. ' +
            'Now the left leg back: long stride, tailbone under, squeeze. ' +
            'Hold… ten… twenty… thirty. ' +
            'Step together and stand tall.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'seated-figure-four',
        name: 'Figure 4 stretch',
        region: 'stretch',
        position: 'seated',
        tags: ['lbp', 'asymmetry'],
        difficulty: 1,
        durationSec: 150,
        reps: '30 s each side, 2 rounds',
        cues: [
            'Cross one ankle over the opposite knee.',
            'Sit tall, then hinge forward from the hips.',
            'Stop at a comfortable stretch in the outer hip.',
        ],
        steps: [
            'Sit toward the front of a sturdy chair with your feet flat.',
            'Cross your right ankle over your left knee and let the right knee drop out to the side.',
            'Sit tall, then hinge forward from the hips with a long back until you feel a stretch in the right buttock and outer hip.',
            'Hold thirty seconds, breathing slowly.',
            'Sit up, uncross, and switch sides.',
        ],
        mistakes: [
            'Hinge from the hips — keep the back long rather than rounding down.',
            'Rest the ankle just past the knee so the knee isn’t twisted.',
            'Let the knee drop on its own instead of pushing it down hard.',
        ],
        precautions: [
            'Skip the forward lean, or rest the ankle lower on the shin, if the crossed knee feels strained.',
            'Ease off if you feel a pinch in the front of the hip.',
        ],
        voiceScript: 'Sit toward the front of your chair, feet flat. ' +
            'Cross your right ankle over your left knee and let the right knee open out. ' +
            'Sit tall. Now hinge forward from your hips, keeping your back long, until you feel a stretch in your right hip. ' +
            'Breathe in… and out. Hold… ten… twenty… thirty. ' +
            'Sit up and uncross. ' +
            'Now the left ankle over the right knee. Sit tall, hinge forward. ' +
            'Hold… ten… twenty… thirty. ' +
            'Sit up, uncross, and place both feet flat.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'hamstring-stretch',
        name: 'Hamstring stretch',
        region: 'stretch',
        position: 'seated',
        tags: ['lbp', 'asymmetry'],
        difficulty: 1,
        durationSec: 150,
        reps: '30 s each side, 2 rounds',
        cues: [
            'Sit on the chair edge, one leg straight, heel down, toes up.',
            'Hinge forward from the hips with a long back.',
            'Feel it along the back of the thigh, then switch.',
        ],
        steps: [
            'Sit toward the front edge of a sturdy chair. Keep your left foot flat and straighten your right leg in front, heel on the floor, toes up.',
            'Sit tall with your hands resting on your thighs.',
            'Hinge forward from the hips, keeping your back long, until you feel a stretch along the back of the right thigh.',
            'Hold thirty seconds, breathing slowly, then sit up and switch legs.',
            'Standing option: rest one heel on a low step, keep that knee soft, and hinge forward the same way.',
        ],
        mistakes: [
            'Lead with the chest — don’t round your back to reach for the toes.',
            'Keep a tiny bend in the straight knee rather than locking it.',
            'Stay at a comfortable stretch rather than bouncing.',
        ],
        precautions: [
            'Ease off if you feel tingling down the leg — the stretch should stay in the back of the thigh.',
            'Use a chair that won’t slide or roll.',
        ],
        voiceScript: 'Sit on the front edge of your chair. ' +
            'Straighten your right leg in front of you, heel on the floor, toes up. Left foot flat. ' +
            'Sit tall, hands on your thighs. ' +
            'Now hinge forward from your hips, chest leading, back long, until you feel the stretch behind your right thigh. ' +
            'Breathe in… and out. Hold… ten… twenty… thirty. ' +
            'Sit up. Switch legs: left leg straight, right foot flat. Hinge forward. ' +
            'Hold… ten… twenty… thirty. ' +
            'Sit up and bring both feet back under you.',
        media: [],
        contraindicationTags: [],
        illustration: 'stretch',
    },
];
/**
 * Retired entries, kept verbatim so history (exercise logs, past coaching and
 * Darwin answers) can still show their names. Not in the library, never picked.
 */
export const ARCHIVED_EXERCISES = [
    {
        id: 'cervical-retraction-hold',
        name: 'Chin tuck with long hold',
        tags: ['fhp'],
        difficulty: 2,
        durationSec: 90,
        reps: '5 reps, 10s hold',
        cues: [
            'Start from a normal chin tuck.',
            'Hold the tucked position for ten seconds without shrugging.',
            'Breathe normally through the hold.',
        ],
        contraindicationTags: ['strong-extension'],
        illustration: 'chin-tuck',
    },
    {
        id: 'suboccipital-release',
        name: 'Base-of-skull release',
        tags: ['fhp'],
        difficulty: 1,
        durationSec: 90,
        reps: '90s',
        cues: [
            'Lie down with a rolled towel under the base of your skull.',
            'Nod your chin down a few millimetres and relax.',
            'Let the weight of your head do the work.',
        ],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'foam-roller-thoracic',
        name: 'Foam roller upper-back extension',
        tags: ['thoracic-ext'],
        difficulty: 2,
        durationSec: 120,
        reps: '8 slow extensions',
        cues: [
            'Lie with a foam roller across your upper back, hands supporting your head.',
            'Extend gently over the roller, then return.',
            'Keep your ribs from flaring and move one segment at a time.',
        ],
        contraindicationTags: ['strong-extension'],
        illustration: 'thoracic-ext',
    },
    {
        id: 'cat-cow',
        name: 'Cat-cow',
        tags: ['thoracic-ext', 'core'],
        difficulty: 1,
        durationSec: 90,
        reps: '10 slow cycles',
        cues: [
            'On hands and knees, stack wrists under shoulders.',
            'Round the whole spine on the exhale.',
            'Open the chest and lift the tailbone on the inhale.',
        ],
        contraindicationTags: [],
        illustration: 'thoracic-ext',
    },
    {
        id: 'prone-ytw',
        name: 'Prone Y-T-W',
        tags: ['scap', 'thoracic-ext'],
        difficulty: 3,
        durationSec: 150,
        reps: '8 reps each letter',
        cues: [
            'Lie face down, forehead resting on a towel.',
            'Lift the arms into a Y, then a T, then a W.',
            'Lead with the shoulder blades, not the hands.',
        ],
        contraindicationTags: ['overhead', 'strong-extension'],
        illustration: 'scap-retract',
    },
    {
        id: 'seated-thoracic-rotation',
        name: 'Seated upper-back rotation',
        tags: ['thoracic-ext', 'asymmetry'],
        difficulty: 1,
        durationSec: 60,
        reps: '8 per side',
        cues: [
            'Sit tall, arms crossed over your chest.',
            'Rotate from the ribs, keeping hips still.',
            'Exhale as you turn, return slowly.',
        ],
        contraindicationTags: [],
        illustration: 'thoracic-ext',
    },
    {
        id: 'band-pull-apart',
        name: 'Band pull-apart',
        tags: ['scap'],
        difficulty: 2,
        durationSec: 90,
        reps: '15 reps',
        cues: [
            'Hold a light band at shoulder height, arms straight.',
            'Pull the band apart until your arms are wide.',
            'Return under control; keep the ribs down.',
        ],
        contraindicationTags: [],
        illustration: 'scap-retract',
    },
    {
        id: 'prone-row',
        name: 'Prone row',
        tags: ['scap'],
        difficulty: 2,
        durationSec: 90,
        reps: '10 reps',
        cues: [
            'Lie face down with arms hanging toward the floor.',
            'Pull the elbows back toward the ribs.',
            'Pause at the top, lower slowly.',
        ],
        contraindicationTags: [],
        illustration: 'scap-retract',
    },
    {
        id: 'serratus-wall-slide',
        name: 'Serratus wall slide',
        tags: ['scap'],
        difficulty: 2,
        durationSec: 90,
        reps: '10 reps',
        cues: [
            'Place your forearms on a wall, elbows at shoulder height.',
            'Slide upward while pushing the wall away.',
            'Let the shoulder blades travel around the ribs.',
        ],
        contraindicationTags: ['overhead'],
        illustration: 'wall-angel',
    },
    {
        id: 'dead-bug',
        name: 'Dead bug',
        tags: ['core'],
        difficulty: 2,
        durationSec: 120,
        reps: '8 per side',
        cues: [
            'Lie on your back, arms up, knees over hips.',
            'Lower one arm and the opposite leg while keeping the lower back flat.',
            'Exhale as you reach, return slowly.',
        ],
        contraindicationTags: [],
        illustration: 'core',
    },
    {
        id: 'bird-dog',
        name: 'Bird dog',
        tags: ['core', 'lbp', 'asymmetry'],
        difficulty: 2,
        durationSec: 120,
        reps: '8 per side',
        cues: [
            'On hands and knees, brace gently through the middle.',
            'Extend one arm and the opposite leg to a straight line.',
            'Keep the hips level; do not let the back sag.',
        ],
        contraindicationTags: [],
        illustration: 'core',
    },
    {
        id: 'glute-bridge',
        name: 'Glute bridge',
        tags: ['core', 'lbp'],
        difficulty: 1,
        durationSec: 90,
        reps: '12 reps',
        cues: [
            'Lie on your back, knees bent, feet flat.',
            'Press through the heels and lift the hips.',
            'Squeeze at the top, lower one vertebra at a time.',
        ],
        contraindicationTags: [],
        illustration: 'core',
    },
    {
        id: 'pelvic-tilt',
        name: 'Pelvic tilt',
        tags: ['core', 'lbp'],
        difficulty: 1,
        durationSec: 60,
        reps: '12 reps',
        cues: [
            'Sit or lie with knees bent.',
            'Roll the pelvis back so the lower back flattens, then forward.',
            'Keep the movement small and slow.',
        ],
        contraindicationTags: [],
        illustration: 'core',
    },
    {
        id: 'ninety-ninety-breathing',
        name: '90/90 breathing',
        tags: ['core'],
        difficulty: 1,
        durationSec: 120,
        reps: '8 breaths',
        cues: [
            'Lie with your feet on a chair, hips and knees at 90°.',
            'Inhale through the nose into the lower ribs.',
            'Exhale fully and let the ribs settle down.',
        ],
        contraindicationTags: [],
        illustration: 'core',
    },
    {
        id: 'side-plank-knees',
        name: 'Side plank from the knees',
        tags: ['core', 'asymmetry'],
        difficulty: 2,
        durationSec: 90,
        reps: '2 sides, 20s each',
        cues: [
            'Lie on your side, elbow under the shoulder, knees bent.',
            'Lift the hips into a straight line from knee to head.',
            'Stop before the hips start to drop.',
        ],
        contraindicationTags: ['load-bearing'],
        illustration: 'core',
    },
    {
        id: 'standing-hip-hinge',
        name: 'Standing hip hinge',
        tags: ['lbp'],
        difficulty: 2,
        durationSec: 90,
        reps: '10 reps',
        cues: [
            'Stand with feet hip width, a light bend in the knees.',
            'Push the hips back, keeping the spine long.',
            'Return by squeezing the glutes.',
        ],
        contraindicationTags: ['load-bearing'],
        illustration: 'stand-up',
    },
    {
        id: 'stand-every-30',
        name: 'Stand up every 30 minutes',
        tags: ['general'],
        difficulty: 1,
        durationSec: 60,
        reps: 'Once per 30 minutes',
        cues: [
            'Stand, take five steps and roll the shoulders once.',
            'Look at something far away for twenty seconds.',
            'Sit back down and reset your setup.',
        ],
        contraindicationTags: [],
        illustration: 'stand-up',
    },
    {
        id: 'eye-break-2020',
        name: '20-20-20 eye break',
        tags: ['general'],
        difficulty: 1,
        durationSec: 30,
        reps: 'Every 20 minutes',
        cues: [
            'Every 20 minutes, look 20 feet away.',
            'Hold for 20 seconds.',
            'Notice whether your head crept toward the screen while you did it.',
        ],
        contraindicationTags: [],
        illustration: 'stand-up',
    },
    {
        id: 'single-leg-glute-bridge',
        name: 'Single-leg glute bridge',
        tags: ['asymmetry', 'core', 'lbp'],
        difficulty: 2,
        durationSec: 90,
        reps: '8 reps each side',
        cues: [
            'Lie on your back, one knee bent, the other leg straight.',
            'Press through the bent leg’s heel and lift your hips level.',
            'Lower slowly, then switch sides.',
        ],
        contraindicationTags: [],
        illustration: 'core',
    },
    {
        id: 'split-squat',
        name: 'Split squat',
        tags: ['asymmetry', 'lbp'],
        difficulty: 2,
        durationSec: 90,
        reps: '8 reps each side',
        cues: [
            'Stand with one foot forward and one back, hip-width apart.',
            'Lower the back knee toward the floor with your torso tall.',
            'Push up through the front foot, then switch sides.',
        ],
        contraindicationTags: [],
        illustration: 'stand-up',
    },
    {
        id: 'one-arm-row',
        name: 'One-arm row',
        tags: ['asymmetry', 'scap'],
        difficulty: 2,
        durationSec: 90,
        reps: '10 reps each side',
        cues: [
            'Hinge forward with one hand on the desk, the other holding a bag.',
            'Pull the bag toward your hip, shoulder blade drawing back.',
            'Lower with control, then switch sides.',
        ],
        contraindicationTags: [],
        illustration: 'scap-retract',
    },
    {
        id: 'suitcase-carry',
        name: 'Suitcase carry',
        tags: ['asymmetry', 'core'],
        difficulty: 2,
        durationSec: 120,
        reps: '30 m each hand',
        cues: [
            'Hold a bag or weight in one hand at your side.',
            'Walk tall without leaning toward the load or away from it.',
            'Switch hands and walk back.',
        ],
        contraindicationTags: [],
        illustration: 'stand-up',
    },
    {
        id: 'pallof-press',
        name: 'Pallof press',
        tags: ['asymmetry', 'core'],
        difficulty: 2,
        durationSec: 90,
        reps: '8 reps each side',
        cues: [
            'Anchor a band at chest height beside you and hold it at your chest.',
            'Press it straight out without letting it turn you.',
            'Bring it back slowly; face the other way and repeat.',
        ],
        contraindicationTags: [],
        illustration: 'core',
    },
    {
        id: 'seated-side-bend',
        name: 'Seated side bend',
        tags: ['asymmetry', 'thoracic-ext'],
        difficulty: 1,
        durationSec: 60,
        reps: '5 slow reps each side',
        cues: [
            'Sit tall with both feet flat and one hand on the seat.',
            'Reach the other arm overhead and lean gently away from it.',
            'Breathe out into the reach, then switch sides.',
        ],
        contraindicationTags: [],
        illustration: 'stretch',
    },
    {
        id: 'single-leg-stand',
        name: 'Single-leg stand',
        tags: ['asymmetry', 'general'],
        difficulty: 1,
        durationSec: 60,
        reps: '30 s each leg',
        cues: [
            'Stand beside the desk so a fingertip can steady you.',
            'Lift one foot and keep your hips level.',
            'Switch legs, and give the wobblier side a little longer.',
        ],
        contraindicationTags: [],
        illustration: 'stand-up',
    },
];
/** Ids valid for new recommendations — the active library only. */
export const EXERCISE_IDS = EXERCISES.map((e) => e.id);
/** Looks up an exercise by id, active first, then archived (names for history). */
export function exerciseById(id) {
    return EXERCISES.find((e) => e.id === id) ?? ARCHIVED_EXERCISES.find((e) => e.id === id);
}
//# sourceMappingURL=exercises.js.map